import { useEffect, useRef } from 'react';

const bookDetails = [
  { icon: 'ri-file-text-line', label: 'Format', value: 'Kindle Edition' },
  { icon: 'ri-pages-line', label: 'Print Length', value: '76 Pages' },
  { icon: 'ri-global-line', label: 'Language', value: 'English' },
  { icon: 'ri-building-line', label: 'Publisher', value: 'Leavitt Peak Press' },
  { icon: 'ri-calendar-line', label: 'Published', value: 'March 18, 2026' },
  { icon: 'ri-barcode-line', label: 'ISBN-13', value: '978-1969865374' },
];

const themes = [
  { icon: 'ri-water-flash-line', label: 'Nature & Rivers', desc: 'The river as a living metaphor for the flow of consciousness and life.' },
  { icon: 'ri-mind-map', label: 'Inner Awakening', desc: 'Each drop of water mirrors a thought — awakening the mind to deeper truths.' },
  { icon: 'ri-heart-pulse-line', label: 'Soul Exploration', desc: 'A penetrating journey into the soul line that connects all living beings.' },
  { icon: 'ri-sun-line', label: 'Light & Transformation', desc: 'Daylight as a symbol of clarity, renewal, and spiritual transformation.' },
];

export default function BookSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = entry.target.querySelectorAll('.reveal-book');
            els.forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    const el = sectionRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, []);

  return (
    <section id="book" className="py-24 md:py-32 bg-stone-900 relative overflow-hidden">
      {/* Decorative texture */}
      <div className="absolute inset-0 opacity-5 pointer-events-none"
        style={{ backgroundImage: 'repeating-linear-gradient(45deg, #d97706 0, #d97706 1px, transparent 0, transparent 50%)', backgroundSize: '20px 20px' }}
      ></div>

      <div ref={sectionRef} className="max-w-7xl mx-auto px-4 md:px-10 relative z-10">
        {/* Header */}
        <div
          className="reveal-book text-center mb-16"
          style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-amber-500/10 text-amber-400 text-xs font-semibold mb-4 border border-amber-500/20 tracking-widest uppercase">
            The Book
          </span>
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-4 leading-tight">
            A River{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-amber-500">
              PENETRATING
            </span>{' '}
            THE Soul Line
          </h2>
          <p className="text-stone-400 text-lg max-w-2xl mx-auto">
            Book #II in the Soul Line series — a lyrical meditation on consciousness, nature, and the river within us all.
          </p>
        </div>

        {/* Main content */}
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">
          {/* Left: Book visual + buy */}
          <div
            className="reveal-book flex-shrink-0 flex flex-col items-center gap-6 w-full lg:w-auto"
            style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
          >
            <div className="relative group">
              <div className="absolute -inset-6 bg-amber-400/10 rounded-3xl blur-2xl group-hover:bg-amber-400/20 transition-all duration-500"></div>
              <img
                src="https://storage.readdy-site.link/project_files/99887cdb-c324-46c8-9a26-303317ec92e6/1df45a8a-1dcb-4b16-8a1a-ff708c3716c6_river-penetrating.jpg?v=b071ab56d87411fda1d5a539ac76f492"
                alt="A River PENETRATING THE Soul Line Book Cover"
                className="relative w-52 md:w-64 h-auto rounded-xl shadow-2xl object-cover object-top transform group-hover:-translate-y-2 transition-transform duration-500"
              />
            </div>

            {/* Pricing */}
            <div className="w-full max-w-xs bg-stone-800 rounded-2xl p-6 border border-stone-700 text-center">
              <div className="flex items-center justify-center gap-3 mb-1">
                <span className="text-stone-500 text-sm line-through">$9.99</span>
                <span className="text-3xl font-bold text-amber-400">$3.99</span>
              </div>
              <p className="text-stone-500 text-xs mb-5">Kindle Edition · Instant Download</p>
              <a
                href="https://www.amazon.com/dp/B0GT29CWSG"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-full bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold text-sm transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-amazon-line text-base"></i>
                Buy on Amazon
              </a>
              <p className="text-stone-600 text-xs mt-3">Also available in Paperback — $9.99</p>
            </div>

            {/* Book details */}
            <div className="w-full max-w-xs space-y-2">
              {bookDetails.map((d) => (
                <div key={d.label} className="flex items-center gap-3 py-2 border-b border-stone-800">
                  <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                    <i className={`${d.icon} text-amber-500 text-sm`}></i>
                  </div>
                  <span className="text-stone-500 text-xs w-24 flex-shrink-0">{d.label}</span>
                  <span className="text-stone-300 text-xs font-medium">{d.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Description + themes */}
          <div className="flex-1">
            {/* Description */}
            <div
              className="reveal-book mb-10"
              style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
            >
              <h3 className="text-xl font-serif font-bold text-amber-300 mb-4">About This Book</h3>
              <div className="space-y-4 text-stone-300 text-base leading-relaxed">
                <p>
                  A river twists, whirls, and bursts across the shore — lavish, unstoppable, alive. In the calm of daylight, it pours again and again, a cascade that mirrors the currents of our own minds.
                </p>
                <p>
                  Each drop awakens thought, stirs the heart, and calls us to dive in. <em>This is a river that does more than flow — it transforms.</em>
                </p>
                <p>
                  In this second installment of the <strong className="text-amber-300">Soul Line</strong> series, Kevin Hamlett continues his exploration of the invisible threads that connect the natural world to the inner life. Through poetic prose and penetrating reflection, he invites readers to sit beside the river — to listen, to feel, and to be changed by what they hear.
                </p>
                <p>
                  Whether you are seeking solace, clarity, or simply a moment of beauty in a busy world, this book offers a quiet place to rest and a deep current to carry you forward.
                </p>
              </div>
            </div>

            {/* Themes */}
            <div
              className="reveal-book"
              style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
            >
              <h3 className="text-xl font-serif font-bold text-amber-300 mb-6">Core Themes</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {themes.map((t) => (
                  <div key={t.label} className="flex gap-4 p-5 rounded-xl bg-stone-800/60 border border-stone-700/50 hover:border-amber-500/30 transition-all duration-300">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-amber-500/10 flex-shrink-0">
                      <i className={`${t.icon} text-amber-400 text-lg`}></i>
                    </div>
                    <div>
                      <div className="text-white font-semibold text-sm mb-1">{t.label}</div>
                      <div className="text-stone-400 text-xs leading-relaxed">{t.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Series note */}
            <div
              className="reveal-book mt-8 p-5 rounded-xl bg-amber-500/5 border border-amber-500/20"
              style={{ opacity: 0, transform: 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
            >
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-amber-400 text-lg mt-0.5 flex-shrink-0"></i>
                <div>
                  <p className="text-amber-300 font-semibold text-sm mb-1">Part of the Soul Line Series</p>
                  <p className="text-stone-400 text-sm leading-relaxed">
                    This is Book #II in Kevin Hamlett's ongoing Soul Line series. Each book stands alone as a complete meditation, while also deepening the themes explored across the series.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
